import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, Loader2, Heart, Building2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const ROLES = [
  { value: "family", label: "Family", desc: "Upload documents & track eligibility", icon: Heart },
  { value: "agency", label: "Agency", desc: "Review documents & verify compliance", icon: Building2 },
];

export default function AuthPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [mode, setMode] = useState("login");
  const [role, setRole] = useState("family");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const payload =
        mode === "login"
          ? { email: form.email, password: form.password }
          : { ...form, role };
      const { data } = await api.post(`/auth/${mode}`, payload);
      login(data);
      toast.success(`Welcome${mode === "register" ? " to Nexari" : " back"}, ${data.user.name}`);
      // If user came from /pricing with intent to subscribe, send them back
      let nextRoute = "/dashboard";
      try {
        if (localStorage.getItem("nexari_intent_plan")) nextRoute = "/pricing";
      } catch (e) { /* noop */ }
      navigate(nextRoute);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-[#E8E6E1] rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C50]/30 focus:border-[#2E5C50] transition-colors duration-200";

  return (
    <div className="min-h-screen bg-[#F9F7F4] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md fade-in-up">
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-8">
          <div className="w-10 h-10 bg-[#2E5C50] rounded-md flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-white" strokeWidth={1.5} />
          </div>
          <span className="font-heading font-bold text-2xl tracking-tight">Nexari Health</span>
        </Link>

        <div className="bg-white border border-[#E8E6E1] rounded-xl p-8 shadow-sm">
          <div className="flex bg-[#F9F7F4] rounded-md p-1 mb-6">
            {["login", "register"].map((m) => (
              <button
                key={m}
                data-testid={`auth-tab-${m}`}
                onClick={() => {
                  setMode(m);
                  setError("");
                }}
                className={`flex-1 py-2 text-sm font-medium rounded transition-colors duration-200 ${
                  mode === m ? "bg-white shadow-sm text-[#2E5C50]" : "text-[#57605A]"
                }`}
              >
                {m === "login" ? "Sign in" : "Create account"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  {ROLES.map((r) => (
                    <button
                      key={r.value}
                      type="button"
                      data-testid={`role-selector-${r.value}`}
                      onClick={() => setRole(r.value)}
                      className={`text-left border rounded-lg p-3 transition-all duration-200 ${
                        role === r.value
                          ? "border-[#2E5C50] bg-[#D6E0D9]/30 ring-1 ring-[#2E5C50]"
                          : "border-[#E8E6E1] hover:border-[#2E5C50]/50"
                      }`}
                    >
                      <r.icon className="w-5 h-5 text-[#2E5C50]" strokeWidth={1.5} />
                      <p className="mt-1.5 font-semibold text-sm text-[#1C211F]">{r.label}</p>
                      <p className="text-xs text-[#57605A] leading-snug">{r.desc}</p>
                    </button>
                  ))}
                </div>
                <input
                  data-testid="register-name-input"
                  required
                  placeholder="Full name"
                  value={form.name}
                  onChange={set("name")}
                  className={inputCls}
                />
              </>
            )}
            <input
              data-testid="auth-email-input"
              required
              type="email"
              placeholder="Email address"
              value={form.email}
              onChange={set("email")}
              className={inputCls}
            />
            <input
              data-testid="auth-password-input"
              required
              type="password"
              placeholder="Password (min 6 characters)"
              minLength={6}
              value={form.password}
              onChange={set("password")}
              className={inputCls}
            />
            {error && (
              <p data-testid="auth-error" className="text-sm text-[#B54D40] bg-[#FCEAE8] rounded-md px-3 py-2">
                {error}
              </p>
            )}
            <button
              data-testid="auth-submit-btn"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-6 py-3 font-medium disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />}
              {mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>
        </div>
        <p className="mt-6 text-center text-xs text-[#57605A]">
          Next-Gen Care, Verified.
        </p>
      </div>
    </div>
  );
}
